import { useState, useEffect, useCallback } from 'react'

const parseCookies = (): Record<string, string> => {
  const cookieMap: Record<string, string> = {}
  if (typeof document === 'undefined') return cookieMap

  document.cookie.split(';').forEach(cookie => {
    const [key, ...val] = cookie.trim().split('=')
    if (key) {
      cookieMap[key] = decodeURIComponent(val.join('='))
    }
  })
  return cookieMap
}

if (typeof window !== 'undefined' && !('hasCookieInterceptor' in window)) {
  const cookieDescriptor =
    Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
    Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie')

  if (cookieDescriptor && cookieDescriptor.set) {
    const nativeCookieSet = cookieDescriptor.set

    Object.defineProperty(document, 'cookie', {
      configurable: true,
      enumerable: true,
      get: function () {
        return cookieDescriptor.get?.call(this) || ''
      },
      set: function (val) {
        nativeCookieSet.call(this, val)
        window.dispatchEvent(new CustomEvent('js-cookie-changed'))
      },
    })
    ;(window as any).hasCookieInterceptor = true
  }
}

export function useCookies(cookieNames?: string[]) {
  const [cookies, setCookies] = useState<Record<string, string>>(parseCookies)

  const syncCookies = useCallback(() => {
    const allCookies = parseCookies()

    if (cookieNames && cookieNames.length > 0) {
      const filtered: Record<string, string> = {}
      cookieNames.forEach(name => {
        if (allCookies[name] !== undefined) filtered[name] = allCookies[name]
      })
      setCookies(filtered)
    } else setCookies(allCookies)
  }, [cookieNames])

  useEffect(() => {
    window.addEventListener('js-cookie-changed', syncCookies)

    if ('cookieStore' in window) {
      ;(window as any).cookieStore.addEventListener('change', syncCookies)
    }

    let intervalId: any
    if (!('cookieStore' in window)) {
      let lastCookieString = document.cookie
      intervalId = setInterval(() => {
        if (document.cookie !== lastCookieString) {
          lastCookieString = document.cookie
          syncCookies()
        }
      }, 500)
    }

    return () => {
      window.removeEventListener('js-cookie-changed', syncCookies)
      if ('cookieStore' in window) {
        ;(window as any).cookieStore.removeEventListener('change', syncCookies)
      }
      if (intervalId) clearInterval(intervalId)
    }
  }, [syncCookies])

  const setCookie = (name: string, value: string, options?: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/${options ? `; ${options}` : ''}`
  }

  const removeCookie = (name: string) => {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
  }

  return [cookies, setCookie, removeCookie] as const
}
